import { useRef } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    TableToolbar,
    TextTool,
    TSTFilterBar,
    TSTPagination,
    TSTTable,
} from "@impero-com/ui";

import { EntityOverviewListDto } from "api/generated/model";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { AssignUsersModal } from "./assignUsers/AssignUsersModal";
import { useAssignUsersUtils } from "./assignUsers/useAssignUsersUtils";
import { useControlEntitiesListTable } from "./useControlEntitiesListTable";

interface ControlEntitiesListProps {
    data: EntityOverviewListDto;
}

export function ControlEntitiesList({ data }: ControlEntitiesListProps) {
    const { t } = useTranslation();
    const { table, getFilterComponent, canAddException, tableId } =
        useControlEntitiesListTable(data);
    const tableElement = useRef<HTMLDivElement>(null);

    const modeConfig = getFilterModeConfig(t);

    const contentByEntityId = new Map(
        data.content.map((content) => [content.resource.id, content])
    );
    const selected = table
        .getFilteredSelectedRowModel()
        .rows.flatMap((row) => contentByEntityId.get(row.original.id) ?? []);

    const {
        assignUsersToolState,
        modalEntities,
        modalScopes,
        isAssignUsersModalOpen,
        openAssignUsersModal,
        closeAssignUsersModal,
        assignUsers,
        isAssigningUsers,
    } = useAssignUsersUtils({
        selected,
        deselectAll: () => table.resetRowSelection(),
    });

    return (
        <div>
            <AssignUsersModal
                open={isAssignUsersModalOpen}
                loading={isAssigningUsers}
                entities={modalEntities}
                scopes={modalScopes}
                onConfirm={assignUsers}
                onClose={closeAssignUsersModal}
            />
            <TSTFilterBar
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                addExceptionLabel={t("FilterBar_AddException")}
                modeConfig={modeConfig}
            />

            <TSTTable
                tableRef={tableElement}
                table={table}
                toolbar={
                    <TableToolbar
                        selection={{
                            count: selected.length,
                            label:
                                selected.length > 0
                                    ? t("TableToolbar_SelectedCount", {
                                          count: selected.length,
                                      })
                                    : t("TableToolbar_NotSelected"),
                        }}
                    >
                        <TextTool
                            state={assignUsersToolState}
                            onClick={openAssignUsersModal}
                        >
                            {t("ControlEntities_ManageAccessRights")}
                        </TextTool>
                    </TableToolbar>
                }
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
            />
            <TSTPagination
                table={table}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
                tableElement={tableElement}
            />
        </div>
    );
}
